import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { storageService } from '@/services/local-storage';
import type { Solution } from '@/types/evaluation.types';
import { EvaluationStatus } from '@/types/evaluation.types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ParticipantsTable } from '@/components/dashboard/ParticipantsTable';
import { EditableTargetDate } from '@/components/dashboard/EditableTargetDate';
import { CompetitiveSolutionsDisplay } from '@/components/dashboard/CompetitiveSolutionsDisplay';
import { CriteriaGroupsTable } from '@/components/dashboard/CriteriaGroupsTable';
import { DemoDataGeneratorDialog } from '@/components/dashboard/DemoDataGeneratorDialog';
import { StatusCard } from '@/components/evaluation-status/StatusCard';
import { ArrowLeft, Calendar, ClipboardList, Play, BarChart3, Info } from 'lucide-react';
import { useEvaluation, useSaveEvaluation, useActivateEvaluation, useConcludeEvaluation, useCloseEvaluation, useReactivateEvaluation } from '@/hooks';

export function EvaluationDashboard() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: evaluation, isLoading } = useEvaluation(id || '');
  const saveEvaluation = useSaveEvaluation();
  const activateEvaluation = useActivateEvaluation();
  const concludeEvaluation = useConcludeEvaluation();
  const closeEvaluation = useCloseEvaluation();
  const reactivateEvaluation = useReactivateEvaluation();

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common:navigation.backToHome')}
          </Link>
        </Button>
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading evaluation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common:navigation.backToHome')}
          </Link>
        </Button>
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {id ? t('dashboard:notFound.evaluationNotFound') : t('dashboard:notFound.noIdProvided')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleManageParticipants = () => {
    navigate(`/evaluation/${id}/participants`);
  };

  const handleManageCriteria = () => {
    navigate(`/evaluation/${id}/criteria`);
  };

  const handleViewResults = () => {
    navigate(`/evaluation/${id}/results`);
  };

  // Calculate total criteria count
  const totalCriteria = evaluation.criteriaGroups.reduce(
    (sum, group) => sum + group.criteria.length,
    0
  );

  const handleUpdateTargetDate = (date: Date | null) => {
    if (evaluation) {
      const updated = { ...evaluation, targetCompletionDate: date };
      saveEvaluation.mutate(updated);
    }
  };

  const handleAddSolution = (solution: Omit<Solution, 'id'>) => {
    if (evaluation) {
      const newSolution: Solution = {
        ...solution,
        id: crypto.randomUUID(),
      };
      const updated = {
        ...evaluation,
        targetSolutions: [...evaluation.targetSolutions, newSolution],
      };
      saveEvaluation.mutate(updated);
    }
  };

  const handleEditSolution = (solutionId: string, updates: Partial<Solution>) => {
    if (evaluation) {
      const updated = {
        ...evaluation,
        targetSolutions: evaluation.targetSolutions.map((sol) =>
          sol.id === solutionId ? { ...sol, ...updates } : sol
        ),
      };
      saveEvaluation.mutate(updated);
    }
  };

  const handleRemoveSolution = (solutionId: string) => {
    if (evaluation) {
      const updated = {
        ...evaluation,
        targetSolutions: evaluation.targetSolutions.filter((sol) => sol.id !== solutionId),
      };
      saveEvaluation.mutate(updated);
    }
  };

  const handleUpdateName = (name: string) => {
    if (evaluation) {
      const updated = {
        ...evaluation,
        name,
        modifiedDate: new Date(),
      };
      saveEvaluation.mutate(updated);
    }
  };

  const handleUpdateSummary = (summary: string | undefined) => {
    if (evaluation) {
      const updated = {
        ...evaluation,
        summary,
        modifiedDate: new Date(),
      };
      saveEvaluation.mutate(updated);
    }
  };

  const handleUpdateAssignments = (participantId: string, criteriaGroupAssignments: string[] | null) => {
    if (evaluation) {
      const updated = {
        ...evaluation,
        participants: evaluation.participants.map((p) =>
          p.id === participantId ? { ...p, criteriaGroupAssignments } : p
        ),
        modifiedDate: new Date(),
      };
      saveEvaluation.mutate(updated);
    }
  };

  const handleStartScoring = () => {
    // Navigate to scorecard for the first solution
    // In a real app, we'd get the current user's participant ID from auth context
    // For now, use the host participant (first participant or create a default one)
    if (evaluation.targetSolutions.length > 0) {
      const firstSolution = evaluation.targetSolutions[0];
      // Find the host participant or use the first participant
      const currentUserParticipant = evaluation.participants.find(p => p.participantType === 'Host') || evaluation.participants[0];
      const participantId = currentUserParticipant?.id || 'current-user';
      navigate(`/evaluation/${id}/scorecard/${firstSolution.id}/${participantId}`);
    }
  };

  // Get the current user's participant (in a real app, this would come from auth context)
  const currentUserParticipant = evaluation.participants.find(p => p.participantType === 'Host') || evaluation.participants[0];
  const currentUserParticipantId = currentUserParticipant?.id || 'current-user';

  // Check if user has any scorecard progress for their participant
  const hasAnyScorecard = evaluation.targetSolutions.some((solution) =>
    storageService.getScorecardBySolution(evaluation.id, solution.id, currentUserParticipantId) !== null
  );

  // Get the first solution with a scorecard, or the first solution overall
  const getScorecardSolution = () => {
    if (evaluation.targetSolutions.length === 0) return null;

    const solutionWithScorecard = evaluation.targetSolutions.find((solution) =>
      storageService.getScorecardBySolution(evaluation.id, solution.id, currentUserParticipantId) !== null
    );

    return solutionWithScorecard || evaluation.targetSolutions[0];
  };

  const scorecardSolution = getScorecardSolution();

  const handleDemoDataGenerated = () => {
    // Force page reload to show updated scorecard data
    window.location.reload();
  };

  // Status action handlers
  const handleActivate = (evaluationToActivate: typeof evaluation) => {
    if (evaluationToActivate) {
      activateEvaluation.mutate(evaluationToActivate);
    }
  };

  const handleConclude = (evaluationToConclude: typeof evaluation) => {
    if (evaluationToConclude) {
      concludeEvaluation.mutate(evaluationToConclude);
    }
  };

  const handleClose = (evaluationToClose: typeof evaluation, reason?: string) => {
    if (evaluationToClose) {
      closeEvaluation.mutate({ evaluation: evaluationToClose, reason });
    }
  };

  const handleReactivate = (evaluationToReactivate: typeof evaluation) => {
    if (evaluationToReactivate) {
      reactivateEvaluation.mutate(evaluationToReactivate);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Back Button */}
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common:navigation.backToHome')}
        </Link>
      </Button>

      <div className="space-y-6">
        {/* Status Card with Evaluation Details and Actions */}
        <StatusCard
        evaluation={evaluation}
        onActivate={handleActivate}
        onConclude={handleConclude}
        onClose={handleClose}
        onReactivate={handleReactivate}
        onUpdateName={handleUpdateName}
        onUpdateSummary={handleUpdateSummary}
      />

      {/* Overview: Target Completion and Solutions */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Target Completion Card - 1/3 width */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('dashboard:overview.targetCompletion')}</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <EditableTargetDate
              date={evaluation.targetCompletionDate}
              onSave={handleUpdateTargetDate}
            />
            {evaluation.status === EvaluationStatus.Organizing && (
              <div className="mt-3 flex items-start gap-2 p-2 rounded-md bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  {t('dashboard:overview.targetCompletionInfo')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Solutions Card - 2/3 width */}
        <Card className="md:col-span-2">
          <CardContent className="pt-6">
            <CompetitiveSolutionsDisplay
              solutions={evaluation.targetSolutions}
              onAdd={handleAddSolution}
              onEdit={handleEditSolution}
              onRemove={handleRemoveSolution}
            />
          </CardContent>
        </Card>
      </div>

      {/* Actions: Evaluation Results & My Scorecard */}
      {evaluation.targetSolutions.length > 0 && totalCriteria > 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* View Results Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-base font-medium">
                  {t('dashboard:evaluationResults.title')}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {t('dashboard:evaluationResults.description')}
              </p>
              <Button onClick={handleViewResults} variant="default" className="w-full sm:w-auto">
                <BarChart3 className="mr-2 h-4 w-4" />
                {t('dashboard:evaluationResults.viewButton')}
              </Button>
            </CardContent>
          </Card>

          {/* My Scorecard Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-base font-medium">
                  {t('dashboard:myScorecard.title')}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {hasAnyScorecard
                  ? t('dashboard:myScorecard.hasScorecardDescription')
                  : t('dashboard:myScorecard.noScorecardDescription')}
              </p>
              {scorecardSolution && (
                <Button onClick={handleStartScoring} className="w-full sm:w-auto">
                  {hasAnyScorecard ? (
                    <>
                      <ClipboardList className="mr-2 h-4 w-4" />
                      {t('dashboard:myScorecard.continueScoringButton')}
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      {t('dashboard:myScorecard.startScoringButton')}
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Participants Table */}
      <Card>
        <CardContent className="pt-6">
          <ParticipantsTable
            participants={evaluation.participants}
            evaluationId={evaluation.id}
            solutions={evaluation.targetSolutions}
            criteriaGroups={evaluation.criteriaGroups}
            evaluationStatus={evaluation.status}
            onManageClick={handleManageParticipants}
            onUpdateAssignments={handleUpdateAssignments}
            demoDataButton={
              <DemoDataGeneratorDialog evaluation={evaluation} onGenerate={handleDemoDataGenerated} />
            }
          />
        </CardContent>
      </Card>

        {/* Criteria Groups Table */}
        <Card>
          <CardContent className="pt-6">
            <CriteriaGroupsTable
              criteriaGroups={evaluation.criteriaGroups}
              evaluationStatus={evaluation.status}
              onManageClick={handleManageCriteria}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
